'use client'

import { useAtom, useAtomValue } from 'jotai'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import { useWidgetConfigSync } from '@/features/dashboard/hooks/use-widget-config-sync'
import {
  formatNumberWithPrecision,
  getPrecisionFromStep,
} from '@/features/order/lib/number-format-util'
import { useUnifiedOrderForm } from '@/features/order/order-form/hooks/use-unified-order-form'
import { getRowGroupKey } from '@/features/order/order-form/lib/row-group-key'
import { registerReloadBlockerSource } from '@/service/runtime-health/reload-blocker-registry'
import { useResetOrderFormCollapsibleConfig } from '@/service/ui-state.hook'
import {
  markOrderFormManual,
  type OrderFormFieldPatch,
  orderFormFieldSignalAtomFamily,
  orderFormOrderbookSignalAtomFamily,
  orderFormSelectionAtomFamily,
  orderFormServerValuesAtomFamily,
} from '@/state/order'

// Re-export types from unified schema
export type { UnifiedOrderMode } from '@/features/order/order-form/lib/unified-order-schema'

// Unified context type (includes cardId for accessing linking-group state)
export type UnifiedOrderFormContextValue = ReturnType<typeof useUnifiedOrderForm> & {
  cardId: string
}

const UnifiedOrderFormContext = createContext<UnifiedOrderFormContextValue | null>(null)

interface OrderFormProviderProps {
  children: ReactNode
  cardId: string
}

// Last symbol each card's form has been shown with; survives provider remounts.
const lastSeenSymbolByCard = new Map<string, string>()

export function OrderFormProvider({ children, cardId }: OrderFormProviderProps) {
  const [selection, setSelection] = useAtom(orderFormSelectionAtomFamily(cardId))
  const [orderbookSignal, setOrderbookSignal] = useAtom(orderFormOrderbookSignalAtomFamily(cardId))
  const [fieldSignal, setFieldSignal] = useAtom(orderFormFieldSignalAtomFamily(cardId))

  const { selectedInstruments, selectedOptions } = selection

  const setSelectedInstruments: React.Dispatch<React.SetStateAction<typeof selectedInstruments>> =
    useCallback(
      (update) => {
        setSelection((prev) => {
          const next = typeof update === 'function' ? update(prev.selectedInstruments) : update
          if (next === prev.selectedInstruments) return prev
          return { ...prev, selectedInstruments: next }
        })
      },
      [setSelection]
    )

  const setSelectedOptions: React.Dispatch<React.SetStateAction<typeof selectedOptions>> =
    useCallback(
      (update) => {
        setSelection((prev) => {
          const next = typeof update === 'function' ? update(prev.selectedOptions) : update
          if (next === prev.selectedOptions) return prev
          return { ...prev, selectedOptions: next }
        })
      },
      [setSelection]
    )

  // The ticket's own lead instrument (its first row) — what "the symbol changed" means for
  // the form's price/TWAP reset and the collapsible-config reset below. Never another
  // widget's focused tab: switching a chart tab must not touch the form.
  const leadInstrument = selectedInstruments[0]
  const leadOption = selectedOptions[0]
  const leadSymbol = leadInstrument?.symbol ?? leadOption?.instrumentName
  const leadExchange = leadInstrument?.exchange ?? leadOption?.exchange ?? ''
  const leadType = leadInstrument ? leadInstrument.instrumentKind : 'options'
  const selectedSymbol = useMemo(
    () =>
      leadSymbol
        ? { id: `${leadExchange}:${leadSymbol}`, symbol: leadSymbol, type: leadType }
        : null,
    [leadExchange, leadSymbol, leadType]
  )

  // Use unified order form hook
  const unifiedForm = useUnifiedOrderForm({
    cardId,
    selectedOptions,
    setSelectedOptions,
    selectedInstruments,
    setSelectedInstruments,
    selectedSymbol,
  })
  const { fields, form } = unifiedForm

  // Read through a ref so the registration effect below doesn't need to
  // re-run (and re-register) on every render — isBlocking() is only called
  // on demand, right before a Level 3 reload, so it just needs to see
  // whatever unifiedForm most recently rendered with.
  const unifiedFormRef = useRef(unifiedForm)
  unifiedFormRef.current = unifiedForm

  // Tells runtime-recovery not to reload out from under unsubmitted order
  // input or an in-flight submit — see hasUnsavedUserEdits' comment in
  // use-unified-order-form.ts for why this isn't just unifiedForm.isDirty.
  useEffect(
    () =>
      registerReloadBlockerSource({
        reason: 'order-ticket-dirty',
        isBlocking: () =>
          unifiedFormRef.current.hasUnsavedUserEdits() || unifiedFormRef.current.isSubmitting,
      }),
    []
  )

  // Server-side form values: restore (once) and save (debounced)
  const { syncConfig } = useWidgetConfigSync()
  const serverValues = useAtomValue(orderFormServerValuesAtomFamily(cardId))
  const serverValuesAppliedRef = useRef(false)

  // Reset collapsible configs when the symbol changes. The first symbol a card sees
  // is the restored one, not a change. Tracked per card, not per mount: the popup
  // remounts this provider on every open and must still reset if the chart switched
  // symbol in between.
  const resetConfig = useResetOrderFormCollapsibleConfig(cardId)

  useEffect(() => {
    if (!selectedSymbol?.id || lastSeenSymbolByCard.get(cardId) === selectedSymbol.id) return
    if (lastSeenSymbolByCard.has(cardId)) resetConfig()
    lastSeenSymbolByCard.set(cardId, selectedSymbol.id)
  }, [cardId, selectedSymbol?.id, resetConfig])

  // Restore: apply server-fetched values once items are populated. `fields`
  // changes only when the field array's structure changes; watching `items`
  // here would re-render this Provider on every amount keystroke forever,
  // even after the one-time restore had already completed.
  useEffect(() => {
    if (serverValuesAppliedRef.current || !serverValues) return
    const formItemsForRestore = form.getValues('items')
    if (formItemsForRestore.length === 0) return
    serverValuesAppliedRef.current = true

    const {
      itemAmounts,
      price: _price,
      priceAnchor: _priceAnchor,
      priceOffsetUnit: _priceOffsetUnit,
      ...restValues
    } = serverValues

    if (Object.keys(restValues).length > 0) {
      const EXCLUDED_TWAP_KEYS = new Set([
        'twapIntervalMinutes',
        'twapFrequencySeconds',
        'twapFrequencyMinSeconds',
        'twapFrequencyMaxSeconds',
        'twapFrequencyMode',
        'twapOrderSize',
        'twapOrderSizeMin',
        'twapOrderSizeMax',
        'twapOrderSizeMode',
        'advancedTwapDurationSeconds',
        'advancedTwapTotalSlices',
        'advancedTwapBandEnabled',
        'advancedTwapBandPct',
        'advancedTwapReduceOnly',
      ])

      const filteredValues = Object.fromEntries(
        Object.entries(restValues).filter(([k]) => !EXCLUDED_TWAP_KEYS.has(k))
      )

      form.reset({ ...form.getValues(), ...filteredValues })
    }

    if (itemAmounts && Object.keys(itemAmounts).length > 0) {
      formItemsForRestore.forEach((item, i) => {
        const savedAmount = itemAmounts[item.symbol]
        if (savedAmount && !item.lockedAmount) {
          form.setValue(`items.${i}.amount`, savedAmount, { shouldDirty: true })
        }
      })
    }
  }, [fields.length, form, serverValues])

  // Inject orderbook price into Limit order form
  // nonce in deps ensures re-trigger on repeated same-value clicks
  useEffect(() => {
    if (!orderbookSignal.price || fields.length === 0) return

    const firstItem = fields[0]
    const firstInstrument = selectedInstruments[0]
    const pricePrecision = getPrecisionFromStep(
      firstItem?.tickSize ?? firstItem?.pricePrecision ?? firstInstrument?.tickSize,
      2
    )

    // Visual-row-based, not raw fields.length — a ticker split across 2+
    // accounts is still one row and stays eligible for an absolute price.
    if (unifiedForm.isSingleVisualRow) {
      form.setValue('priceAnchor', 'price', { shouldDirty: true })
    }
    form.setValue('price', formatNumberWithPrecision(orderbookSignal.price, pricePrecision), {
      shouldValidate: true,
      shouldDirty: true,
    })
    // An injected price (orderbook click, chart BUY/SELL or "Buy at price") is final — mark
    // the field synced so the ticker-driven auto-sync retry (useUnifiedOrderForm's
    // `!hasSyncedRef` path, re-run on every ticker update while unsynced) can't replace it a
    // moment later with the first leg's side quote.
    unifiedForm.markPriceSynced()
    // Injected from outside: the ticket is the user's now (see markOrderFormManual).
    markOrderFormManual(cardId)
    setOrderbookSignal((prev) => ({ ...prev, price: null }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields,
    form,
    orderbookSignal.price,
    orderbookSignal.nonce,
    selectedInstruments,
    unifiedForm.isSingleVisualRow,
  ])

  // Inject orderbook amount into order form (always into the first VISUAL
  // row — every member sharing fields[0]'s (exchange, itemType, symbol), not
  // just index 0 itself, or the injected amount would only land on one leg
  // of a split multi-account first row while its other accounts kept their
  // old amount — see row-group-key.ts.
  useEffect(() => {
    if (orderbookSignal.amount && fields.length > 0) {
      const firstRowKey = getRowGroupKey(fields[0], unifiedForm.primaryExchange)
      const firstRowIndexes = fields
        .map((field, index) => ({ field, index }))
        .filter(({ field }) => getRowGroupKey(field, unifiedForm.primaryExchange) === firstRowKey)
        .map(({ index }) => index)
      unifiedForm.handleAmountChangeForGroup(firstRowIndexes, orderbookSignal.amount)
      setOrderbookSignal((prev) => ({ ...prev, amount: null }))
    }
    // handleAmountChangeForGroup is read from the render that delivered the signal; keying on
    // it (or on the whole unifiedForm) would only re-run this guard on every form render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, orderbookSignal.amount, orderbookSignal.nonce, unifiedForm.primaryExchange])

  // Inject prompt-bar field values (TWAP duration, reduce-only…). Waits for fields like the price
  // signal above, so a ticker-change form reset in the same commit can't wipe it.
  useEffect(() => {
    if (!fieldSignal.patch || fields.length === 0) return
    for (const [key, value] of Object.entries(fieldSignal.patch)) {
      if (value === undefined) continue
      form.setValue(key as keyof OrderFormFieldPatch, value, { shouldDirty: true })
    }
    markOrderFormManual(cardId)
    setFieldSignal((prev) => ({ ...prev, patch: null }))
  }, [cardId, fields.length, fieldSignal.patch, fieldSignal.nonce, form, setFieldSignal])

  // Stable while unifiedForm is, so consumers don't re-render on every provider render.
  const contextValue = useMemo(() => ({ ...unifiedForm, cardId }), [unifiedForm, cardId])

  return (
    <UnifiedOrderFormContext.Provider value={contextValue}>
      {children}
    </UnifiedOrderFormContext.Provider>
  )
}

/**
 * Hook to access the unified order form context
 */
export function useUnifiedOrderFormContext() {
  const context = useContext(UnifiedOrderFormContext)
  if (!context) {
    throw new Error('useUnifiedOrderFormContext must be used within OrderFormProvider')
  }
  return context
}

// Legacy alias for backward compatibility
export const useOrderFormContext = useUnifiedOrderFormContext

// Helper functions for mode checking
function isMultiLeg(ctx: UnifiedOrderFormContextValue): boolean {
  return ctx.mode === 'multi-leg'
}

function isSingleLeg(ctx: UnifiedOrderFormContextValue): boolean {
  return ctx.mode === 'single-leg'
}

function isGeneralMode(ctx: UnifiedOrderFormContextValue): boolean {
  return ctx.mode === 'general'
}
